import React from 'react';
import { GraduationCap, Users, BarChart3, CheckCircle, Zap, Wifi, MessageSquare } from 'lucide-react';

// 🤖 Componente para mostrar respuestas de IA - SISTEMA DE ORQUESTACIÓN POR EVENTOS
const AIResponseCard = ({ 
    response, 
    aiRole = "tutor", 
    aiSource = "fallback",
    isLoading = false,
    evaluation = null,       // 📊 Datos de evaluación con scores (legacy)
    approved = null,         // 📊 Estado de aprobación (legacy)
    contractType = "text",   // 🎯 NUEVO: Tipo de contrato desde JSON (text, structured_text, json)
    gating = null,           // 🚪 NUEVO: Información de gating pedagógico
    content = null           // 📄 NUEVO: Contenido normalizado desde backend
}) => {
    // 🎭 Configuración por rol
    const roleConfig = {
        tutor: {
            icon: GraduationCap,
            title: "Feedback del Tutor IA",
            color: "indigo",
            description: "Análisis pedagógico de tu hipótesis"
        },
        sparring_cliente: {
            icon: Users, 
            title: "Simulación de Cliente",
            color: "purple",
            description: "Reacción simulada del cliente objetivo"
        },
        analista: {
            icon: BarChart3,
            title: "Análisis de Datos",
            color: "blue", 
            description: "Interpretación estadística y métricas"
        },
        evaluador: {
            icon: CheckCircle,
            title: "Evaluación de Progreso", 
            color: "green",
            description: "Valoración del avance y siguiente paso"
        }
    };

    const config = roleConfig[aiRole] || roleConfig.tutor;
    
    // 🎨 Classes dinámicas por color
    const colorClasses = {
        indigo: "from-indigo-500 to-indigo-600 border-indigo-200 bg-indigo-50",
        purple: "from-purple-500 to-purple-600 border-purple-200 bg-purple-50", 
        blue: "from-blue-500 to-blue-600 border-blue-200 bg-blue-50",
        green: "from-green-500 to-green-600 border-green-200 bg-green-50"
    };

    // 📋 RENDERIZADO POR CONTRACT TYPE (según lineamiento JSON)
    const renderContent = () => {
        switch (contractType) {
            case 'json':
                return renderJSONContent();
            case 'structured_text':
                return renderStructuredText();
            case 'text':
            default:
                return renderTextContent();
        }
    };

    // 📝 Renderizado para contract type: "text" con auto-detección JSON
    const renderTextContent = () => {
        let responseText = content || response || "Sin contenido disponible";
        
        // 🔍 Auto-detectar y formatear JSON
        if (typeof responseText === 'string') {
            try {
                // Intentar parsear como JSON
                const parsed = JSON.parse(responseText);
                return (
                    <div className="prose prose-sm max-w-none">
                        <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                            <pre className="text-green-400 text-sm font-mono leading-relaxed">
                                {JSON.stringify(parsed, null, 2)}
                            </pre>
                        </div>
                    </div>
                );
            } catch (e) {
                // No es JSON válido, mostrar como texto normal
            }
        }
        
        // 🔍 Handle JSON objects ya parseados
        if (typeof responseText === 'object' && responseText !== null) {
            return (
                <div className="prose prose-sm max-w-none">
                    <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                        <pre className="text-green-400 text-sm font-mono leading-relaxed">
                            {JSON.stringify(responseText, null, 2)}
                        </pre>
                    </div>
                </div>
            );
        }
        
        // 📄 Texto normal
        return (
            <div className="prose prose-sm max-w-none">
                <div className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {responseText}
                </div>
            </div>
        );
    };

    // 🏗️ Renderizado para contract type: "structured_text" 
    const renderStructuredText = () => {
        let sectionsData = content || response || "";
        
        // 🔍 Handle JSON objects
        if (typeof sectionsData === 'object' && sectionsData !== null && !Array.isArray(sectionsData)) {
            sectionsData = JSON.stringify(sectionsData, null, 2);
        }
        
        const sections = Array.isArray(sectionsData) ? sectionsData : [sectionsData];
        
        return (
            <div className="space-y-4">
                {sections.map((section, index) => (
                    <div key={index} className="bg-gray-50 p-4 rounded-lg">
                        <div className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                            {section}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    // 📊 Renderizado para contract type: "json" (evaluaciones con scores)
    const renderJSONContent = () => {
        if (!content || typeof content !== 'object') {
            return renderTextContent();
        }

        const { fortaleza, mejora, accion, scores, veredicto } = content;
        const isApproved = veredicto === 'aprobado' || (gating && gating.ok);
        
        return (
            <div className="space-y-4">
                {/* 🏆 Resultado principal con gating */}
                <div className={`p-4 rounded-lg border-2 ${
                    isApproved 
                        ? 'bg-green-50 border-green-200' 
                        : 'bg-red-50 border-red-200'
                }`}>
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                isApproved ? 'bg-green-500' : 'bg-red-500'
                            }`}>
                                {isApproved ? (
                                    <CheckCircle className="w-5 h-5 text-white" />
                                ) : (
                                    <span className="text-white font-bold text-sm">✗</span>
                                )}
                            </div>
                            <span className={`font-semibold ${
                                isApproved ? 'text-green-800' : 'text-red-800'
                            }`}>
                                {isApproved ? '¡Hipótesis Aprobada!' : 'Hipótesis Requiere Mejoras'}
                            </span>
                        </div>
                        <div className={`text-2xl font-bold ${
                            isApproved ? 'text-green-600' : 'text-red-600'
                        }`}>
                            {scores?.total || 0}/100
                        </div>
                    </div>
                    
                    {!isApproved && gating && (
                        <p className="text-red-700 text-sm">
                            Necesitas al menos {gating.min_score || 70} puntos para continuar al siguiente paso.
                        </p>
                    )}
                </div>

                {/* 📊 Desglose de criterios desde JSON */}
                {scores && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {Object.entries(scores)
                            .filter(([key]) => key !== 'total')
                            .map(([key, score]) => {
                                const criteriaNames = {
                                    claridad: 'Claridad',
                                    foco_cliente: 'Foco Cliente', 
                                    viabilidad: 'Viabilidad',
                                    evidencia: 'Evidencia',
                                    tecnico: 'Técnico',
                                    modelo: 'Modelo'
                                };
                                
                                return (
                                    <div key={key} className="bg-white p-3 rounded-lg border border-gray-200">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-sm font-medium text-gray-700">
                                                {criteriaNames[key] || key}
                                            </span>
                                            <span className={`text-sm font-bold ${
                                                score >= 70 ? 'text-green-600' : 'text-red-600'
                                            }`}>
                                                {score}/100
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div 
                                                className={`h-2 rounded-full ${
                                                    score >= 70 ? 'bg-green-500' : 'bg-red-500'
                                                }`}
                                                style={{ width: `${Math.min(score, 100)}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                );
                            })}
                    </div>
                )}

                {/* 📝 Análisis detallado del tutor */}
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                        <h4 className="font-medium text-gray-800 flex items-center gap-2">
                            <MessageSquare className="w-4 h-4" />
                            Análisis Detallado del Tutor
                        </h4>
                    </div>
                    
                    <div className="p-4 space-y-3">
                        {fortaleza && (
                            <div>
                                <strong className="text-green-700">1) Fortaleza:</strong>
                                <p className="text-gray-700 mt-1">{fortaleza}</p>
                            </div>
                        )}
                        
                        {mejora && (
                            <div>
                                <strong className="text-orange-700">2) Mejora:</strong>
                                <p className="text-gray-700 mt-1">{mejora}</p>
                            </div>
                        )}
                        
                        {accion && (
                            <div>
                                <strong className="text-blue-700">3) Acción sugerida:</strong>
                                <p className="text-gray-700 mt-1">{accion}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // ⏳ Loading state
    if (isLoading) {
        return (
            <div className="max-w-4xl mx-auto mt-6 p-6 bg-white rounded-xl shadow-lg border border-gray-200">
                <div className="flex items-center gap-3 mb-4">
                    <div className={`w-10 h-10 rounded-full bg-gradient-to-r ${colorClasses[config.color].split(' ')[0]} ${colorClasses[config.color].split(' ')[1]} flex items-center justify-center`}>
                        <config.icon className="w-5 h-5 text-white animate-pulse" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-gray-800">{config.title}</h3>
                        <p className="text-sm text-gray-600">Analizando tu hipótesis...</p>
                    </div>
                </div>
                
                {/* Loading skeleton */}
                <div className="space-y-3">
                    <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                    <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse"></div>
                </div>
            </div>
        );
    }

    // 📝 Respuesta real
    return (
        <div className="max-w-4xl mx-auto mt-6 p-6 bg-white rounded-xl shadow-lg border border-gray-200">
            {/* 🎯 Header con rol e indicador de fuente */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full bg-gradient-to-r ${colorClasses[config.color].split(' ')[0]} ${colorClasses[config.color].split(' ')[1]} flex items-center justify-center`}>
                        <config.icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-gray-800">{config.title}</h3>
                        <p className="text-sm text-gray-600">{config.description}</p>
                    </div>
                </div>
                
                {/* 🔗 Chip de fuente IA */}
                <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                    (aiSource === 'openai' || aiSource === 'ai') 
                        ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                        : 'bg-amber-100 text-amber-700 border border-amber-200'
                }`}>
                    {(aiSource === 'openai' || aiSource === 'ai') ? <Zap className="w-3 h-3" /> : <Wifi className="w-3 h-3" />}
                    <span>{(aiSource === 'openai' || aiSource === 'ai') ? 'OpenAI' : 'Offline'}</span>
                </div>
            </div>

            {/* � RENDERIZADO POR CONTRACT TYPE - Sistema de Orquestación por Eventos */}
            {renderContent()}
            
            {/* 🔄 Footer con metadata del sistema de orquestación */}
            <div className="mt-4 pt-3 border-t border-gray-100 flex justify-between items-center text-xs text-gray-500">
                <div className="flex items-center gap-3">
                    <span>Rol: <span className="font-medium">{aiRole}</span></span>
                    <span>Contract: <span className="font-medium">{contractType}</span></span>
                    {gating && gating.reason && (
                        <span>Gating: <span className={`font-medium ${gating.ok ? 'text-green-600' : 'text-red-600'}`}>
                            {gating.ok ? 'Pass' : gating.reason}
                        </span></span>
                    )}
                </div>
                <span>Generado: {new Date().toLocaleString('es-ES')}</span>
            </div>
        </div>
    );
};

export default AIResponseCard;